import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Trash2, Users, Plus, Calendar, IdCard, ClipboardList, Truck, QrCode, BarChart3, FileDown, Copy } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CAT_LABEL = { expositor: "Expositor", palestrante: "Palestrante", participante: "Participante", staff: "Staff" };
const CAT_COLOR = { expositor: "#3B82F6", palestrante: "#D4AF37", participante: "#71717A", staff: "#10B981" };

export default function EventEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [form, setForm] = useState({ title: "", client_name: "", kind: "corporativo", starts_at: "", ends_at: "", location: "", description: "", audience_size: 0 });
  const [vendors, setVendors] = useState([]);
  const [newPart, setNewPart] = useState({ name: "", email: "", document: "", company: "", role: "", category: "participante" });
  const [newItem, setNewItem] = useState({ category: "buffet", name: "", vendor_id: "", notes: "", done: false });
  const [live, setLive] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get(`/events/${id}`);
    setEvent(r.data);
    setForm({ ...r.data });
    const v = await api.get("/vendors"); setVendors(v.data);
    const l = await api.get(`/events/${id}/attendance-live`); setLive(l.data);
  }, [id]);
  useEffect(() => { load(); const t = setInterval(() => api.get(`/events/${id}/attendance-live`).then((r) => setLive(r.data)).catch(() => {}), 15000); return () => clearInterval(t); }, [id, load]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    await api.put(`/events/${id}`, { ...form, audience_size: +form.audience_size });
    toast.success("Salvo");
    load();
  };
  const remove = async () => { await api.delete(`/events/${id}`); navigate("/events"); };
  const addParticipant = async () => {
    if (!newPart.name) return toast.error("Nome obrigatório");
    const r = await api.post(`/events/${id}/participants`, [newPart]);
    setEvent((e) => ({ ...e, participants: r.data }));
    setNewPart({ name: "", email: "", document: "", company: "", role: "", category: "participante" });
  };
  const removeParticipant = async (pid) => { await api.delete(`/events/${id}/participants/${pid}`); load(); };
  const addChecklist = async () => {
    if (!newItem.name) return toast.error("Nome do item obrigatório");
    const r = await api.post(`/events/${id}/checklist`, newItem);
    setEvent((e) => ({ ...e, checklist: r.data }));
    setNewItem({ category: "buffet", name: "", vendor_id: "", notes: "", done: false });
  };
  const updateChecklist = async (iid, patch) => {
    const r = await api.patch(`/events/${id}/checklist/${iid}`, patch);
    setEvent((e) => ({ ...e, checklist: r.data }));
  };
  const removeChecklist = async (iid) => { const r = await api.delete(`/events/${id}/checklist/${iid}`); setEvent((e) => ({ ...e, checklist: r.data })); };

  if (!event) return <div className="p-10 overline">Carregando…</div>;
  const checkinUrl = `${window.location.origin}/checkin-evento/${event.checkin_token}`;
  const npsUrl = `${window.location.origin}/nps-evento/${event.nps_token}`;

  return (
    <div className="p-10 max-w-[1400px]">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="overline">{event.event_id.slice(0, 12)}</p>
          <h1 className="font-heading text-4xl mt-2 flex items-center gap-3"><Calendar size={28} strokeWidth={1.3} className="text-[#C07C88]" />{form.title || "Evento"}</h1>
        </div>
        <div className="flex gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" className="border-red-200 text-red-600" data-testid="delete-event-btn"><Trash2 size={14} className="mr-1" /> Excluir</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Excluir evento?</AlertDialogTitle><AlertDialogDescription>Ação permanente.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction className="bg-red-600 text-white" onClick={remove}>Excluir</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button className="bg-[#C07C88] hover:bg-[#A86571]" onClick={save} data-testid="save-event-btn">Salvar</Button>
        </div>
      </div>

      <Tabs defaultValue="info">
        <TabsList className="mb-6 bg-white border border-border">
          <TabsTrigger value="info">Info</TabsTrigger>
          <TabsTrigger value="participants"><Users size={13} className="mr-1" /> Participantes</TabsTrigger>
          <TabsTrigger value="checklist"><ClipboardList size={13} className="mr-1" /> Checklist</TabsTrigger>
          <TabsTrigger value="badges"><IdCard size={13} className="mr-1" /> Crachás</TabsTrigger>
          <TabsTrigger value="attendance"><QrCode size={13} className="mr-1" /> Presença ao vivo</TabsTrigger>
          <TabsTrigger value="nps"><BarChart3 size={13} className="mr-1" /> NPS</TabsTrigger>
          <TabsTrigger value="report">Relatório</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <Card className="p-6 shadow-none border-border">
            <p className="overline mb-4">Informações do Evento</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><Label>Título</Label><Input value={form.title} onChange={(e) => update("title", e.target.value)} data-testid="ev-title" /></div>
              <div><Label>Cliente</Label><Input value={form.client_name} onChange={(e) => update("client_name", e.target.value)} /></div>
              <div>
                <Label>Tipo</Label>
                <select value={form.kind} onChange={(e) => update("kind", e.target.value)} className="w-full h-10 px-3 border border-input rounded-md text-sm bg-white">
                  <option value="corporativo">Corporativo</option><option value="palestra">Palestra</option><option value="workshop">Workshop</option><option value="congresso">Congresso</option>
                </select>
              </div>
              <div><Label>Início</Label><Input type="datetime-local" value={(form.starts_at || "").slice(0, 16)} onChange={(e) => update("starts_at", e.target.value)} /></div>
              <div><Label>Fim</Label><Input type="datetime-local" value={(form.ends_at || "").slice(0, 16)} onChange={(e) => update("ends_at", e.target.value)} /></div>
              <div><Label>Local</Label><Input value={form.location} onChange={(e) => update("location", e.target.value)} /></div>
              <div><Label>Vagas totais</Label><Input type="number" value={form.audience_size} onChange={(e) => update("audience_size", e.target.value)} /></div>
              <div className="md:col-span-2"><Label>Descrição</Label><Textarea rows={4} value={form.description} onChange={(e) => update("description", e.target.value)} /></div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="participants">
          <Card className="p-6 shadow-none border-border">
            <p className="overline mb-4">Participantes ({event.participants.length})</p>
            <div className="grid grid-cols-12 gap-2 mb-4">
              <Input className="col-span-3" placeholder="Nome" value={newPart.name} onChange={(e) => setNewPart({ ...newPart, name: e.target.value })} data-testid="ep-name" />
              <Input className="col-span-2" placeholder="E-mail" value={newPart.email} onChange={(e) => setNewPart({ ...newPart, email: e.target.value })} />
              <Input className="col-span-2" placeholder="Empresa" value={newPart.company} onChange={(e) => setNewPart({ ...newPart, company: e.target.value })} />
              <Input className="col-span-2" placeholder="Cargo" value={newPart.role} onChange={(e) => setNewPart({ ...newPart, role: e.target.value })} />
              <select className="col-span-2 h-10 px-3 border border-input rounded-md text-sm bg-white" value={newPart.category} onChange={(e) => setNewPart({ ...newPart, category: e.target.value })} data-testid="ep-category">
                <option value="participante">Participante</option><option value="expositor">Expositor</option><option value="palestrante">Palestrante</option><option value="staff">Staff</option>
              </select>
              <Button className="col-span-1 bg-[#C07C88]" onClick={addParticipant} data-testid="add-ep-btn"><Plus size={14} /></Button>
            </div>
            <div className="divide-y divide-border">
              {event.participants.map((p) => (
                <div key={p.participant_id} className="py-2 flex items-center gap-3">
                  <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ background: `${CAT_COLOR[p.category]}20`, color: CAT_COLOR[p.category] }}>{CAT_LABEL[p.category]}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{p.company} · {p.role}</p>
                  </div>
                  {p.checked_in_at && <span className="text-xs text-emerald-600">✓ Presente</span>}
                  <button onClick={() => removeParticipant(p.participant_id)} className="p-1.5 text-muted-foreground hover:text-red-500"><Trash2 size={13} /></button>
                </div>
              ))}
              {event.participants.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Sem participantes</p>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="checklist">
          <Card className="p-6 shadow-none border-border">
            <p className="overline mb-4">Checklist Operacional ({event.checklist.length})</p>
            <div className="grid grid-cols-12 gap-2 mb-4">
              <select className="col-span-3 h-10 px-3 border border-input rounded-md text-sm bg-white" value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}>
                <option value="buffet">Buffet</option><option value="som">Som</option><option value="projecao">Projeção</option><option value="credenciamento">Credenciamento</option>
                <option value="cenografia">Cenografia</option><option value="instrutores">Instrutores</option><option value="midia">Mídia</option><option value="comunicacao">Comunicação</option>
                <option value="brindes">Brindes</option><option value="camisas">Camisas</option><option value="outros">Outros</option>
              </select>
              <Input className="col-span-5" placeholder="Nome do item" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} data-testid="cl-name" />
              <select className="col-span-3 h-10 px-3 border border-input rounded-md text-sm bg-white" value={newItem.vendor_id} onChange={(e) => setNewItem({ ...newItem, vendor_id: e.target.value })}>
                <option value="">Sem fornecedor</option>
                {vendors.map((v) => (<option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>))}
              </select>
              <Button className="col-span-1 bg-[#C07C88]" onClick={addChecklist} data-testid="add-cl-btn"><Plus size={14} /></Button>
            </div>
            <div className="divide-y divide-border">
              {event.checklist.map((it) => (
                <div key={it.item_id} className="py-2 flex items-center gap-3">
                  <Checkbox checked={it.done} onCheckedChange={(v) => updateChecklist(it.item_id, { done: !!v })} data-testid={`cl-check-${it.item_id}`} />
                  <span className="text-[9px] px-2 py-0.5 rounded-full bg-muted uppercase tracking-wider">{it.category}</span>
                  <p className={`flex-1 text-sm truncate ${it.done ? "line-through text-muted-foreground" : ""}`}>{it.name}</p>
                  <select className="text-xs h-8 px-2 border border-input rounded-md bg-white" value={it.vendor_id || ""} onChange={(e) => updateChecklist(it.item_id, { vendor_id: e.target.value })}>
                    <option value="">— fornecedor —</option>
                    {vendors.map((v) => (<option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>))}
                  </select>
                  <button onClick={() => removeChecklist(it.item_id)} className="p-1.5 text-muted-foreground hover:text-red-500"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="badges">
          <Card className="p-6 shadow-none border-border">
            <p className="overline mb-4">Crachás em PDF</p>
            <p className="text-sm text-muted-foreground mb-4">Gere PDF com todos os crachás em folha A4 (2×5), agrupados por categoria (Expositor / Palestrante / Participante / Staff) com faixa de cor identificadora.</p>
            <Button asChild className="bg-[#D4AF37] hover:bg-[#B8961F] text-white" data-testid="badges-pdf-btn">
              <a href={`${BACKEND}/api/events/${id}/badges/pdf`} target="_blank" rel="noreferrer"><FileDown size={14} className="mr-2" /> Baixar Crachás PDF</a>
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="attendance">
          {live && (
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <Card className="p-6 shadow-none border-border"><p className="overline">Presentes</p><p className="font-heading text-4xl mt-2 text-emerald-600">{live.present}</p></Card>
              <Card className="p-6 shadow-none border-border"><p className="overline">Ausentes</p><p className="font-heading text-4xl mt-2 text-red-500">{live.absent}</p></Card>
              <Card className="p-6 shadow-none border-border"><p className="overline">Taxa de adesão</p><p className="font-heading text-4xl mt-2 text-[#D4AF37]">{live.attendance_pct}%</p></Card>
            </div>
          )}
          <Card className="p-6 shadow-none border-border">
            <p className="overline mb-3">Link de Check-in (QR)</p>
            <div className="flex gap-2 mb-4">
              <Input readOnly value={checkinUrl} className="text-xs" data-testid="checkin-url" />
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(checkinUrl); toast.success("Copiado"); }}><Copy size={13} /></Button>
              <Button size="sm" variant="outline" asChild><a href={checkinUrl} target="_blank" rel="noreferrer">Abrir</a></Button>
            </div>
            <p className="text-xs text-muted-foreground">Compartilhe este link ou projete o QR na entrada do evento. Cada participante escolhe seu nome e confirma presença.</p>
          </Card>
        </TabsContent>

        <TabsContent value="nps">
          <Card className="p-6 shadow-none border-border mb-4">
            <p className="overline mb-3">Link de Pesquisa NPS (QR)</p>
            <div className="flex gap-2 mb-2">
              <Input readOnly value={npsUrl} className="text-xs" data-testid="nps-url" />
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(npsUrl); toast.success("Copiado"); }}><Copy size={13} /></Button>
              <Button size="sm" variant="outline" asChild><a href={npsUrl} target="_blank" rel="noreferrer">Abrir</a></Button>
            </div>
            <p className="text-xs text-muted-foreground">Exiba no encerramento ou envie por e-mail/WhatsApp. Coleta notas de palestrante, conteúdo, infra e NPS geral.</p>
          </Card>
          <Card className="p-6 shadow-none border-border">
            <p className="overline mb-3">Respostas ({event.nps_responses?.length || 0})</p>
            {event.nps_responses?.length > 0 ? (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase text-muted-foreground"><th className="p-2">Nome</th><th>Palestrante</th><th>Conteúdo</th><th>Infra</th><th>Geral</th><th>Comentário</th></tr></thead>
                <tbody>
                  {event.nps_responses.map((r) => (
                    <tr key={r.response_id} className="border-t border-border">
                      <td className="p-2">{r.name || "Anônimo"}</td>
                      <td>{r.speaker_score}</td><td>{r.content_score}</td><td>{r.infra_score}</td>
                      <td className="text-[#D4AF37] font-semibold">{r.overall_score}</td>
                      <td className="text-xs text-muted-foreground">{r.comment}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="text-xs text-muted-foreground text-center py-6">Sem respostas ainda</p>}
          </Card>
        </TabsContent>

        <TabsContent value="report">
          <Card className="p-6 shadow-none border-border">
            <p className="overline mb-3">Relatório Executivo (Post-Event)</p>
            <p className="text-sm text-muted-foreground mb-4">PDF completo com número de participantes, taxa de adesão, absenteísmo, consolidação NPS (palestrante/conteúdo/infra), status do checklist com fornecedores e comentários dos participantes.</p>
            <Button asChild className="bg-[#C07C88] hover:bg-[#A86571]" data-testid="report-pdf-btn">
              <a href={`${BACKEND}/api/events/${id}/post-event-report/pdf`} target="_blank" rel="noreferrer"><FileDown size={14} className="mr-2" /> Baixar Relatório PDF</a>
            </Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
